import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faPlay, faClockRotateLeft,
  faToggleOn, faToggleOff, faClock, faCheck, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import './CronJobsManagement.css';

/**
 * CronJobsManagement — admin UI for the generic admin_cron_jobs system.
 *
 * Lists all jobs with their last-run state, lets super-admins create /
 * edit / delete / enable / manually trigger / view run history.
 *
 * Form fields are split into:
 *   - Common (name, type, cron, account, channel, output bucket/prefix)
 *   - Type-specific (driven by /api/v2/admin/cron-jobs/types — each type
 *     declares its own field schema, no front-end-only knowledge of types)
 *
 * Adding a new job_type requires zero changes here — once the backend
 * registry has it and a Python handler exists, this UI renders the
 * form fields automatically.
 */
function CronJobsManagement({ userId, refreshTrigger }) {
  const [jobs, setJobs] = useState([]);
  const [types, setTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(emptyFormData());

  const [showHistoryFor, setShowHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Per-field upload state for type:'file' config fields. Keyed by field name
  // so a single form can host more than one upload widget without entangling
  // their status lines.
  const [uploadStatus, setUploadStatus] = useState({});

  function emptyFormData() {
    return {
      name: '',
      job_type: '',
      cron_expression: '0 2 * * *',
      enabled: true,
      account_id: '',
      channel_id: '',
      output_gcs_bucket: 'wowai-dev-chat',
      output_gcs_prefix: 'caches/mmgy',
      config: {},
    };
  }

  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);
      const [jobsRes, typesRes, accountsRes] = await Promise.all([
        api.get(getApiUrl('/api/v2/admin/cron-jobs'), { params: { userId } }),
        api.get(getApiUrl('/api/v2/admin/cron-jobs/types'), { params: { userId } }),
        api.get(getApiUrl('/api/admin/accounts'), { params: { userId } }),
      ]);
      setJobs(jobsRes.data?.jobs || []);
      setTypes(typesRes.data?.types || []);
      setAccounts(accountsRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load cron jobs page', err);
      setError(err.response?.data?.error || err.message || 'Failed to load cron jobs');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshTrigger]);

  // Re-fetch channels whenever the form's selected account changes.
  useEffect(() => {
    if (!showModal || !formData.account_id) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(getApiUrl('/api/v2/admin/cron-jobs/channels'), {
          params: { userId, accountId: formData.account_id },
        });
        if (!cancelled) setChannels(res.data?.channels || []);
      } catch (err) {
        console.warn('Failed to load channels for account', err);
        if (!cancelled) setChannels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, showModal, formData.account_id]);

  function openCreateModal() {
    setEditingJob(null);
    setFormData(emptyFormData());
    setUploadStatus({});
    setFormError(null);
    setShowModal(true);
  }

  function openEditModal(job) {
    setEditingJob(job);
    setFormData({
      name: job.name,
      job_type: job.job_type,
      cron_expression: job.cron_expression,
      enabled: job.enabled,
      account_id: job.account_id,
      channel_id: job.channel_id || '',
      output_gcs_bucket: job.output_gcs_bucket,
      output_gcs_prefix: job.output_gcs_prefix,
      config: typeof job.config === 'string' ? JSON.parse(job.config) : (job.config || {}),
    });
    setUploadStatus({});
    setFormError(null);
    setShowModal(true);
  }

  async function uploadAsset(fieldName, file) {
    if (!file) return;
    if (!formData.output_gcs_bucket) {
      setUploadStatus(s => ({ ...s, [fieldName]: { state: 'error', message: 'Set Output GCS Bucket before uploading.' } }));
      return;
    }
    setUploadStatus(s => ({ ...s, [fieldName]: { state: 'uploading', filename: file.name } }));
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('output_gcs_bucket', formData.output_gcs_bucket);
      body.append('output_gcs_prefix', formData.output_gcs_prefix || '');
      const res = await api.post(
        getApiUrl('/api/v2/admin/cron-jobs/upload-asset'),
        body,
        { params: { userId }, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const gcsPath = res.data?.gcs_path;
      if (!gcsPath) throw new Error('Upload succeeded but server returned no gcs_path');
      setFormData(d => ({ ...d, config: { ...d.config, [fieldName]: gcsPath } }));
      setUploadStatus(s => ({ ...s, [fieldName]: { state: 'done', filename: file.name, gcs_path: gcsPath } }));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setUploadStatus(s => ({ ...s, [fieldName]: { state: 'error', message: msg } }));
    }
  }

  async function saveJob(e) {
    if (e) e.preventDefault();
    setIsSaving(true);
    setFormError(null);
    try {
      const body = { ...formData };
      // Convert brandIds textarea to array if it came back as a string blob.
      if (body.config && typeof body.config.brandIds === 'string') {
        body.config = {
          ...body.config,
          brandIds: body.config.brandIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean),
        };
      }
      if (editingJob) {
        await api.patch(getApiUrl(`/api/v2/admin/cron-jobs/${editingJob.id}`), body, { params: { userId } });
      } else {
        await api.post(getApiUrl('/api/v2/admin/cron-jobs'), body, { params: { userId } });
      }
      setShowModal(false);
      await fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Failed to save cron job');
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteJob(job) {
    if (!window.confirm(`Delete cron job '${job.name}'? This cannot be undone.`)) return;
    try {
      await api.delete(getApiUrl(`/api/v2/admin/cron-jobs/${job.id}`), { params: { userId } });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete cron job');
    }
  }

  async function toggleEnabled(job) {
    try {
      await api.patch(getApiUrl(`/api/v2/admin/cron-jobs/${job.id}`), { enabled: !job.enabled }, { params: { userId } });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to toggle cron job');
    }
  }

  async function runNow(job) {
    if (!window.confirm(`Trigger '${job.name}' now? This dispatches the job immediately.`)) return;
    try {
      await api.post(getApiUrl(`/api/v2/admin/cron-jobs/${job.id}/run`), {}, { params: { userId } });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to run cron job');
    }
  }

  async function showHistory(job) {
    setShowHistoryFor(job);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await api.get(getApiUrl(`/api/v2/admin/cron-jobs/${job.id}/runs`), { params: { userId } });
      setHistoryRows(res.data?.runs || []);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to load run history');
    } finally {
      setHistoryLoading(false);
    }
  }

  const selectedTypeDef = types.find(t => t.key === formData.job_type) || null;
  const filteredChannels = selectedTypeDef
    ? channels.filter(c => c.channel_type_name === selectedTypeDef.requiredChannelType)
    : channels;

  if (isLoading) {
    return (
      <div className="admin-page-content loading-container">
        <div className="loading-content"><p>Loading cron jobs…</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page-content loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchAll}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-content">
      <div className="admin-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} /> New Cron Job
        </button>
      </div>

      {jobs.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faClock} className="empty-state-icon" />
          <p className="empty-state-text">No cron jobs yet</p>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} /> Create First Cron Job
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Cron</th>
                <th>Next Run</th>
                <th>Channel</th>
                <th>Last Run</th>
                <th>Status</th>
                <th>Output</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.id}>
                  <td>{job.name}</td>
                  <td>{(types.find(t => t.key === job.job_type) || {}).label || job.job_type}</td>
                  <td><code>{job.cron_expression}</code></td>
                  <td title="Computed from cron expression in server local time">
                    {job.next_run_at ? new Date(job.next_run_at).toLocaleString() : '—'}
                  </td>
                  <td>{job.channel_name || '—'}</td>
                  <td>
                    {job.last_run_at ? new Date(job.last_run_at).toLocaleString() : '—'}
                    {job.last_run_triggered_by === 'manual' && (
                      <span
                        className="badge badge-manual"
                        title="This run was manually triggered via the Run-now button, not by the cron schedule."
                        style={{ marginLeft: 6, padding: '1px 6px', fontSize: '0.7em', borderRadius: 4, backgroundColor: '#f0ad4e', color: '#fff' }}
                      >
                        manual
                      </span>
                    )}
                    {job.last_run_triggered_by === 'cron' && (
                      <span
                        className="badge badge-cron"
                        title="This run was triggered by the cron schedule."
                        style={{ marginLeft: 6, padding: '1px 6px', fontSize: '0.7em', borderRadius: 4, backgroundColor: '#5bc0de', color: '#fff' }}
                      >
                        cron
                      </span>
                    )}
                  </td>
                  <td>{renderStatusPill(job.last_run_status, job.last_run_error)}</td>
                  <td className="output-path">
                    <code>gs://{job.output_gcs_bucket}/{job.output_gcs_prefix}/{job.name}/</code>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => toggleEnabled(job)}
                      title={job.enabled ? 'Disable' : 'Enable'}
                    >
                      <FontAwesomeIcon icon={job.enabled ? faToggleOn : faToggleOff} />
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-sm btn-success" onClick={() => runNow(job)} title="Run now">
                        <FontAwesomeIcon icon={faPlay} />
                      </button>
                      <button className="btn btn-sm btn-info" onClick={() => showHistory(job)} title="Run history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      <button className="btn btn-sm btn-primary" onClick={() => openEditModal(job)} title="Edit">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteJob(job)} title="Delete">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '720px' }}>
            <h3>{editingJob ? `Edit '${editingJob.name}'` : 'New Cron Job'}</h3>
            <form onSubmit={saveJob}>
              {formError && <div className="alert alert-danger">{formError}</div>}

              <div className="form-row">
                <div className="form-group form-col">
                  <label>Name (unique)</label>
                  <input type="text" className="form-control" value={formData.name} required
                         onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div className="form-group form-col">
                  <label>Job Type</label>
                  <select className="form-control" value={formData.job_type} required
                          onChange={e => {
                            const nextType = e.target.value;
                            const def = types.find(t => t.key === nextType);
                            setFormData({ ...formData, job_type: nextType, channel_id: '', config: defaultConfigForType(def) });
                          }}>
                    <option value="">— select —</option>
                    {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-col">
                  <label>Cron expression</label>
                  <input type="text" className="form-control" value={formData.cron_expression} required
                         onChange={e => setFormData({ ...formData, cron_expression: e.target.value })} />
                  <small className="form-text">Standard 5-field cron. Example: <code>0 2 * * *</code> (daily at 02:00 UTC)</small>
                </div>
                <div className="form-group form-col">
                  <label>Account</label>
                  <select className="form-control" value={formData.account_id} required
                          onChange={e => setFormData({ ...formData, account_id: e.target.value, channel_id: '' })}>
                    <option value="">— select —</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-col">
                  <label>Channel</label>
                  <select className="form-control" value={formData.channel_id}
                          onChange={e => setFormData({ ...formData, channel_id: e.target.value })}
                          disabled={!selectedTypeDef || !formData.account_id}>
                    <option value="">— select —</option>
                    {filteredChannels.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <small className="form-text">
                    {!formData.account_id ? 'Pick an account first'
                      : !selectedTypeDef ? 'Pick a job type first'
                      : `Filtered to ${selectedTypeDef.requiredChannelType} channels enabled on this account`}
                  </small>
                </div>
                <div className="form-group form-col">
                  <label>Output GCS Bucket</label>
                  <input type="text" className="form-control" value={formData.output_gcs_bucket} required
                         onChange={e => setFormData({ ...formData, output_gcs_bucket: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-col">
                  <label>Output GCS Prefix</label>
                  <input type="text" className="form-control" value={formData.output_gcs_prefix} required
                         onChange={e => setFormData({ ...formData, output_gcs_prefix: e.target.value })} />
                  <small className="form-text">
                    Files land at <code>gs://{'{bucket}'}/{'{prefix}'}/{'{job_name}'}/latest.json</code>
                  </small>
                </div>
                <div className="form-group form-col" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input id="cron-job-enabled" type="checkbox" checked={formData.enabled}
                         onChange={e => setFormData({ ...formData, enabled: e.target.checked })} />
                  <label htmlFor="cron-job-enabled" style={{ marginBottom: 0 }}>Enabled</label>
                </div>
              </div>

              {selectedTypeDef && selectedTypeDef.configFormFields && selectedTypeDef.configFormFields.length > 0 && (
                <>
                  <h4 style={{ marginTop: '20px', marginBottom: '12px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                    {selectedTypeDef.label} configuration
                  </h4>
                  {chunk(selectedTypeDef.configFormFields, 2).map((row, rowIdx) => (
                    <div key={rowIdx} className="form-row">
                      {row.map(f => (
                        <div key={f.name} className="form-group form-col">
                          <label>{f.label}{f.required ? ' *' : ''}</label>
                          {f.type === 'textarea' ? (
                            <textarea
                              className="form-control"
                              rows={4}
                              value={renderConfigValue(formData.config[f.name], f)}
                              onChange={e => setFormData({ ...formData, config: { ...formData.config, [f.name]: e.target.value } })}
                            />
                          ) : f.type === 'file' ? (
                            <>
                              <input
                                type="file"
                                className="form-control"
                                accept={f.accept || undefined}
                                onChange={e => uploadAsset(f.name, e.target.files?.[0])}
                                disabled={uploadStatus[f.name]?.state === 'uploading'}
                              />
                              {renderUploadStatus(uploadStatus[f.name], formData.config[f.name])}
                            </>
                          ) : (
                            <input
                              type={f.type === 'number' ? 'number' : 'text'}
                              className="form-control"
                              value={formData.config[f.name] ?? (f.default ?? '')}
                              onChange={e => setFormData({ ...formData, config: { ...formData.config, [f.name]: f.type === 'number' ? Number(e.target.value) : e.target.value } })}
                            />
                          )}
                          {f.helper && <small className="form-text">{f.helper}</small>}
                        </div>
                      ))}
                      {row.length === 1 && <div className="form-group form-col" />}
                    </div>
                  ))}
                </>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn btn-success" disabled={isSaving}>
                  <FontAwesomeIcon icon={faCheck} /> {isSaving ? 'Saving…' : (editingJob ? 'Update' : 'Create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHistoryFor && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '900px' }}>
            <h3>Run history — {showHistoryFor.name}</h3>
            {historyLoading ? (
              <p>Loading…</p>
            ) : historyRows.length === 0 ? (
              <p>No runs yet.</p>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>Status</th>
                      <th>Trigger</th>
                      <th>Output / Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map(r => (
                      <tr key={r.id}>
                        <td>{new Date(r.started_at).toLocaleString()}</td>
                        <td>{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</td>
                        <td>{renderStatusPill(r.status, r.error_message)}</td>
                        <td>{r.triggered_by}</td>
                        <td className="output-path">
                          {r.error_message
                            ? <span className="error-text">{r.error_message}</span>
                            : <code>{r.output_gcs_path || '—'}</code>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowHistoryFor(null)}>
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- helpers ----------------------------------------------------------------

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Seed formData.config with each field's declared `default` when a job type
// is selected. The config inputs only DISPLAY `f.default` as a fallback
// (value={formData.config[name] ?? f.default}); they never commit it to state
// unless the user edits the field. Without this, a pre-filled-but-untouched
// required field (e.g. advertiserId) submits as undefined and the backend
// rejects it with "<label> is required" even though the box looked populated.
function defaultConfigForType(typeDef) {
  const cfg = {};
  for (const f of typeDef?.configFormFields || []) {
    if (f.default !== undefined) cfg[f.name] = f.default;
  }
  return cfg;
}

function renderStatusPill(status, error) {
  // Use the existing status-pill class family from AdminPage.css if present;
  // fall back to inline colour-coded chips otherwise.
  const map = {
    completed: { className: 'status-pill status-completed', label: 'Completed' },
    failed:    { className: 'status-pill status-failed',    label: 'Failed' },
    running:   { className: 'status-pill status-running',   label: 'Running' },
    skipped:   { className: 'status-pill status-skipped',   label: 'Skipped' },
  };
  const m = map[status] || { className: 'status-pill', label: status || '—' };
  return <span className={m.className} title={error || ''}>{m.label}</span>;
}

function renderConfigValue(v, field) {
  if (field.type === 'textarea' && Array.isArray(v)) return v.join('\n');
  return v ?? '';
}

function renderUploadStatus(status, existingPath) {
  // Three states surfaced inline under the file input:
  //   - actively uploading
  //   - error from the last attempt
  //   - done OR the form already has a saved gcs_path from a prior edit
  if (status?.state === 'uploading') {
    return <small className="form-text" style={{ color: '#888' }}>Uploading {status.filename}…</small>;
  }
  if (status?.state === 'error') {
    return <small className="form-text" style={{ color: '#b00' }}>Upload failed: {status.message}</small>;
  }
  const path = status?.gcs_path || existingPath;
  if (path) {
    return (
      <small className="form-text" style={{ color: '#070' }}>
        {status?.state === 'done' ? 'Uploaded → ' : 'Current: '}<code>{path}</code>
      </small>
    );
  }
  return null;
}

export default CronJobsManagement;
